<pt-feature-info />
<pt-breadcrumbs class="bg-white" [breadcrumbs]="breadcrumbs$ | async" />
<div class="border-b border-solid border-slate-300"></div>

<form novalidate [formGroup]="form">
  <div class="mx-auto flex max-w-5xl flex-col gap-4 p-4">
    <pr-content-container>
      <h2 class="text-xl font-bold">General Settings</h2>

      <div class="flex flex-col gap-4">
        <mat-form-field class="w-full">
          <mat-label>Name</mat-label>
          <input matInput formControlName="name" [required]="true" />
        </mat-form-field>

        <mat-form-field class="w-full">
          <mat-label>Email</mat-label>
          <input
            matInput
            type="email"
            formControlName="email"
            [required]="true"
          />
        </mat-form-field>

        <mat-form-field class="w-full">
          <mat-label>Phone</mat-label>
          <input matInput formControlName="phone" [required]="true" />
        </mat-form-field>

        <mat-form-field class="w-full">
          <mat-label>Website URL</mat-label>
          <input matInput formControlName="websiteUrl" type="url" />
          @if ((websiteUrlValid$ | async) === 'valid') {
            <mat-hint>
              <span class="text-success-500">
                <mat-icon class="align-text-top">check_circle</mat-icon>
                <span>Valid URL</span>
              </span>
            </mat-hint>
          }
          @if ((websiteUrlValid$ | async) === 'invalid') {
            <mat-hint>
              <span class="text-warn-500">
                <mat-icon class="align-text-top">error</mat-icon>
                <span>Invalid URL, please include the protocol (e.g. https://example.com)</span>
              </span>
            </mat-hint>
          }
          <mat-error>Please enter a valid URL (e.g. https://example.com)</mat-error>
        </mat-form-field>

        <div class="flex gap-4" formGroupName="settings">
          <mat-form-field class="w-full">
            <mat-label>Timezone</mat-label>
            <mat-select formControlName="timezone" [required]="true">
              <mat-option
                *ngFor="let timezone of timezones; trackBy: trackByTimezone"
                [value]="timezone"
              >
                {{ timezone }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <pr-address-input formControlName="address" [required]="true" />

        <pr-map-preview [coordinates]="coordinates$ | async" />

        <div
          formGroupName="settings"
          class="mt-2 flex flex-row items-center gap-2"
        >
          <mat-form-field
            class="flex grow"
            subscriptSizing="dynamic"
            floatLabel="always"
          >
            <mat-label>Default Fee Schedule</mat-label>
            <mat-select
              formControlName="defaultFeeSchedule"
              [compareWith]="isSameFeeSchedule"
              [placeholder]="unselectedFeeScheduleLabel$ | async"
            >
              <mat-select-trigger>
                {{ selectedFeeScheduleLabel$ | async }}
              </mat-select-trigger>

              <mat-option [value]="undefined">
                {{ unselectedFeeScheduleLabel$ | async }}
              </mat-option>

              @for (group of feeScheduleGroups$ | async; track group.name) {
                <div class="p-2 text-xs font-bold opacity-75">
                  {{ group.name | titlecase }}
                  <mat-icon
                    class="icon-3 opacity-50"
                    matTooltip="Fee Schedules from {{
                      group.scope | titlecase
                    }}"
                    >info</mat-icon
                  >
                </div>
                @for (
                  feeSchedule of group.schedules;
                  track feeSchedule.ref.path
                ) {
                  <mat-option [value]="feeSchedule.ref">
                    {{ feeSchedule.name }}
                  </mat-option>
                }
              }
            </mat-select>
            @if (staleFeeScheduleSelected$ | async) {
              <mat-hint class="text-warn-500">
                The selected fee schedule is no longer available. Please select a
                different one.
              </mat-hint>
            }
          </mat-form-field>
          <mat-icon
            class="opacity-50"
            matTooltip="This fee schedule will be used for new treatment plans created at this practice unless the patient has a preferred fee schedule set"
            >info</mat-icon
          >
        </div>

        @if (billingEntitiesEnabled$ | async) {
          <pr-content-container>
            <div class="flex flex-col gap-4">
              <div class="flex items-center justify-between">
                <h2 class="text-xl font-bold">Billing Entities</h2>
                <button
                  mat-flat-button
                  matTooltip="Add new Billing Entity"
                  color="primary"
                  (click)="billingEntityControls.addControl()"
                >
                  Add Billing Entity
                </button>
              </div>

              <div class="flex flex-col gap-2">
                @if (!billingEntities.controls.length) {
                  <pr-empty-state type="text" title="billing entities" />
                }

                @for (
                  control of billingEntities.controls;
                  track $index;
                  let index = $index;
                  let isFirst = $first
                ) {
                  <pr-billing-entity-input [form]="control">
                    <div class="flex gap-2">
                      @if (!isFirst) {
                        <button
                          mat-stroked-button
                          matTooltip="Remove Billing Entity"
                          color="warn"
                          (click)="billingEntityControls.removeControl(index)"
                        >
                          Remove
                        </button>
                      }
                    </div>
                  </pr-billing-entity-input>
                }
              </div>
            </div>
          </pr-content-container>
        }
      </div>
    </pr-content-container>

    <pr-content-container>
      <div class="flex items-center justify-between gap-4">
        <h2 class="text-xl font-bold">Document Settings</h2>
        <pt-loader-button
          type="stroked"
          [loading]="pdfService.generatingPdf()"
          (clicked)="printTest()"
        >
          @if (pdfService.generatingPdf()) {
            Creating Preview
          } @else {
            Preview Print Settings
          }
        </pt-loader-button>
      </div>
      <p class="text-sm text-gray-500">
        Configure defaults for printed patient documents. Unset values use
        system defaults.
      </p>

      <div class="flex flex-col gap-4">
        <div class="flex flex-row items-center gap-2">
          <mat-form-field
            class="flex grow"
            subscriptSizing="dynamic"
            floatLabel="always"
          >
            <mat-label>Font Family</mat-label>
            <mat-select
              [formControl]="documentSettingsForm.controls.fontFamily"
              [placeholder]="'System Default (' + defaultFontFamily + ')'"
            >
              <mat-option [value]="undefined"> System Default </mat-option>

              @for (font of curatedFonts; track font.fontFamily) {
                <mat-option [value]="font.fontFamily">
                  <span [style.font-family]="font.fontFamily">
                    {{ font.name }}
                  </span>
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-icon
            class="opacity-50"
            matTooltip="The font used for printed patient documents"
            >info</mat-icon
          >
        </div>

        <div class="flex flex-row items-center gap-2">
          <mat-form-field
            class="flex grow"
            subscriptSizing="dynamic"
            floatLabel="always"
          >
            <mat-label>Font Size</mat-label>
            <mat-select
              placeholder="System Default (11pt)"
              [formControl]="documentSettingsForm.controls.fontSize"
            >
              <mat-option [value]="undefined"> System Default </mat-option>
              @for (size of fontSizePresets; track size) {
                <mat-option [value]="size">{{ size }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-icon
            class="opacity-50"
            matTooltip="The base text size used for printed patient documents"
            >info</mat-icon
          >
        </div>

        <div class="flex flex-row items-center gap-2">
          <mat-form-field
            class="flex grow"
            subscriptSizing="dynamic"
            floatLabel="always"
          >
            <mat-label>Line Height</mat-label>
            <mat-select
              placeholder="System Default (1.5)"
              [formControl]="documentSettingsForm.controls.lineHeight"
            >
              <mat-option [value]="undefined"> System Default </mat-option>
              @for (height of lineHeightPresets; track height) {
                <mat-option [value]="height">{{ height }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-icon
            class="opacity-50"
            matTooltip="The vertical space between lines of text within a paragraph"
            >info</mat-icon
          >
        </div>

        <div class="flex flex-row items-center gap-2">
          <mat-form-field
            class="flex grow"
            subscriptSizing="dynamic"
            floatLabel="always"
          >
            <mat-label>Paragraph Spacing</mat-label>
            <mat-select
              placeholder="System Default (0.5em)"
              [formControl]="documentSettingsForm.controls.paragraphSpacing"
            >
              <mat-option [value]="undefined"> System Default </mat-option>
              @for (spacing of paragraphSpacingPresets; track spacing) {
                <mat-option [value]="spacing">{{ spacing }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-icon
            class="opacity-50"
            matTooltip="The space added after each paragraph"
            >info</mat-icon
          >
        </div>

        <h3 class="text-base font-semibold">Margins (mm)</h3>
        <div class="grid grid-cols-2 gap-4">
          <mat-form-field subscriptSizing="dynamic" floatLabel="always">
            <mat-label>Top</mat-label>
            <mat-select
              placeholder="System Default (15mm)"
              [formControl]="documentSettingsForm.controls.marginTop"
            >
              <mat-option [value]="undefined"> System Default </mat-option>
              @for (margin of marginPresets; track margin) {
                <mat-option [value]="margin">{{ margin }}mm</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field subscriptSizing="dynamic" floatLabel="always">
            <mat-label>Bottom</mat-label>
            <mat-select
              placeholder="System Default (15mm)"
              [formControl]="documentSettingsForm.controls.marginBottom"
            >
              <mat-option [value]="undefined"> System Default </mat-option>
              @for (margin of marginPresets; track margin) {
                <mat-option [value]="margin">{{ margin }}mm</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field subscriptSizing="dynamic" floatLabel="always">
            <mat-label>Left</mat-label>
            <mat-select
              placeholder="System Default (15mm)"
              [formControl]="documentSettingsForm.controls.marginLeft"
            >
              <mat-option [value]="undefined"> System Default </mat-option>
              @for (margin of marginPresets; track margin) {
                <mat-option [value]="margin">{{ margin }}mm</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field subscriptSizing="dynamic" floatLabel="always">
            <mat-label>Right</mat-label>
            <mat-select
              placeholder="System Default (15mm)"
              [formControl]="documentSettingsForm.controls.marginRight"
            >
              <mat-option [value]="undefined"> System Default </mat-option>
              @for (margin of marginPresets; track margin) {
                <mat-option [value]="margin">{{ margin }}mm</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <h3 class="text-base font-semibold">Page Layout</h3>
        <div class="grid grid-cols-2 gap-4">
          <mat-form-field subscriptSizing="dynamic" floatLabel="always">
            <mat-label>Paper Size</mat-label>
            <mat-select
              placeholder="System Default (A4)"
              [formControl]="documentSettingsForm.controls.paperSize"
            >
              <mat-option [value]="undefined"> System Default </mat-option>
              @for (size of paperSizePresets; track size) {
                <mat-option [value]="size">{{ size }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field subscriptSizing="dynamic" floatLabel="always">
            <mat-label>Orientation</mat-label>
            <mat-select
              placeholder="System Default (Portrait)"
              [formControl]="documentSettingsForm.controls.orientation"
            >
              <mat-option [value]="undefined"> System Default </mat-option>
              @for (orient of orientationPresets; track orient) {
                <mat-option [value]="orient">
                  {{ orient | titlecase }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      </div>
    </pr-content-container>

    <pr-content-container>
      <h2 class="text-xl font-bold">Branding Settings</h2>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-4">
          @if (practiceLogoUrl$ | async; as practiceLogoUrl) {
            <pt-image-preview [src]="practiceLogoUrl" />
          }
          <div class="relative">
            <p
              class="absolute -top-3 left-3 bg-white p-1 text-xs text-gray-500"
            >
              Practice Logo
            </p>
            @if (practiceLogoUrl$ | async) {
              <div
                class="absolute right-1 top-[3px] z-50"
                matTooltip="Remove Logo"
              >
                <button mat-icon-button (click)="removeLogo()">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            }
            <div class="rounded border border-solid border-gray-300">
              <pt-image-upload
                label="Upload Logo"
                [menuButton]="true"
                [uploadPath]="logoUploadPath$ | async"
                (imageUpload)="updateImageUrl($event)"
              />
            </div>
          </div>
        </div>
      </div>
    </pr-content-container>

    <pr-content-container margin="mb-2" class="sticky bottom-0 z-50">
      <pt-buttons-container padding="none">
        <button
          mat-flat-button
          color="primary"
          [disabled]="!form.valid"
          (click)="save()"
        >
          Save Changes
        </button>
      </pt-buttons-container>
    </pr-content-container>
  </div>
</form>
